import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Routes, Route } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Upload, FileText, Loader2, Sparkles, ShieldCheck, RefreshCw } from "lucide-react";
import BackButton from "@/components/business/BackButton";
import MetricCard from "@/components/business/MetricCard";
import PageHeader from "@/components/business/PageHeader";
import LoadingState from "@/components/business/LoadingState";
import RiskBadge, { riskTextClass } from "@/components/business/RiskBadge";

const STATUS_LABEL = { open: "Open", analyzed: "Analyzed", drafted: "Drafted", responded: "Responded", closed: "Closed" };

export default function Notices() {
  return (
    <Routes>
      <Route index element={<NoticesList />} />
      <Route path=":noticeId" element={<NoticeDetail />} />
    </Routes>
  );
}

function NoticesList() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const fileRef = useRef();

  const load = async () => {
    const [{ data: n }, { data: s }] = await Promise.all([
      api.get("/notices"),
      api.get("/notices/summary/dashboard"),
    ]);
    setRows(n); setSummary(s);
  };
  useEffect(() => {
    load();
    api.get("/practice/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const upload = async () => {
    if (!file) return toast.error("Choose a PDF");
    const fd = new FormData();
    fd.append("file", file);
    if (clientId) fd.append("client_id", clientId);
    setBusy(true);
    try {
      await api.post("/notices/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      toast.success("Notice uploaded — AI analysis complete");
      setFile(null); setClientId(""); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader>
          <PageHeader.Title>Notice Management</PageHeader.Title>
          <PageHeader.Actions>
            <Badge variant="outline"><Sparkles className="h-3 w-3 mr-1" />AI classified</Badge>
          </PageHeader.Actions>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">Upload an ITD notice PDF. Our engine identifies type, AY, sections, deadline, risk and drafts a plain-English explanation + suggested response for your review.</p>

        {summary && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
            <MetricCard label="Total" value={summary.total} testId="notices-stat-total" />
            <MetricCard label="Open" value={summary.by_status.open || 0} testId="notices-stat-open" />
            <MetricCard label="Analyzed" value={summary.by_status.analyzed || 0} testId="notices-stat-analyzed" />
            <MetricCard label="Drafted" value={summary.by_status.drafted || 0} testId="notices-stat-drafted" />
            <MetricCard label="High risk" value={summary.by_risk.high || 0} tone={summary.by_risk.high > 0 ? "danger" : "default"} testId="notices-stat-risk" />
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <Card className="p-6 border-border">
            <div className="font-display font-semibold text-lg flex items-center gap-2"><Upload className="h-4 w-4 text-primary" />Upload notice</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Client (optional)</Label>
                <Select value={clientId || "none"} onValueChange={(v) => setClientId(v === "none" ? "" : v)}>
                  <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="Link to a client" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No client link —</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Notice PDF</Label>
                <Input ref={fileRef} type="file" accept=".pdf,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-10 mt-1.5" data-testid="notice-file" />
              </div>
            </div>
            <Button onClick={upload} disabled={busy || !file} className="mt-4" data-testid="notice-upload-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing…</> : <><Upload className="h-4 w-4 mr-2" />Upload &amp; analyze</>}
            </Button>
          </Card>

          <Card className="p-5 border-border">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Upcoming deadlines</div>
            <div className="mt-3 space-y-1.5">
              {(summary?.upcoming || []).length === 0 && <div className="text-xs text-muted-foreground">No upcoming deadlines.</div>}
              {(summary?.upcoming || []).map(u => (
                <Link key={u.id} to={`/notices/${u.id}`} className="block rounded border border-border p-2 text-xs hover:bg-muted">
                  <div className="font-semibold">{u.notice_type || "Notice"} · {u.client_name || "client"}</div>
                  <div className={`text-[11px] ${riskTextClass(u.risk_level)}`}>Due {u.deadline} · {u.risk_level} risk</div>
                </Link>
              ))}
            </div>
          </Card>
        </div>

        <Card className="mt-6 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Type</th><th className="text-left p-3">Client</th><th className="text-left p-3">AY</th><th className="text-left p-3">Deadline</th><th className="text-left p-3">Risk</th><th className="text-left p-3">Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No notices uploaded yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-semibold">{r.notice_type || "—"}</td>
                  <td className="p-3">{r.client_name || "—"}</td>
                  <td className="p-3">{r.assessment_year || "—"}</td>
                  <td className="p-3">{r.deadline || "—"}</td>
                  <td className="p-3"><RiskBadge level={r.risk_level} variant="text" /></td>
                  <td className="p-3"><Badge variant="outline">{STATUS_LABEL[r.status] || r.status}</Badge></td>
                  <td className="p-3 text-right"><Button asChild variant="outline" size="sm"><Link to={`/notices/${r.id}`}>Open</Link></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}


function NoticeDetail() {
  const { noticeId } = useParams();
  const nav = useNavigate();
  const [n, setN] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("");

  const load = () => api.get(`/notices/${noticeId}`).then(({ data }) => {
    setN(data); setDraft(data.drafted_response || data.suggested_response || ""); setStatus(data.status);
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [noticeId]);

  if (!n) return <AppShell><LoadingState /></AppShell>;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/notices/${noticeId}`, { drafted_response: draft, status });
      toast.success("Saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const reanalyze = async () => {
    setBusy(true);
    try { await api.post(`/notices/${noticeId}/reanalyze`); toast.success("Re-analyzed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <BackButton to="/notices" label="Notices" />
        <PageHeader>
          <div>
            <PageHeader.Title eyebrow={`Notice · AY ${n.assessment_year || "—"}`}>{n.notice_type || "Unclassified"}</PageHeader.Title>
            <div className="mt-2 text-sm text-muted-foreground">{n.file_name}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline">{STATUS_LABEL[n.status] || n.status}</Badge>
              <RiskBadge level={n.risk_level} />
              {n.deadline && <span className="text-xs">Deadline: <span className="font-mono">{n.deadline}</span></span>}
              {n.amount_demanded ? <span className="text-xs">Amount: ₹{Number(n.amount_demanded).toLocaleString()}</span> : null}
            </div>
          </div>
          <PageHeader.Actions>
            <Button variant="outline" onClick={reanalyze} disabled={busy}><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Re-analyze</Button>
          </PageHeader.Actions>
        </PageHeader>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-5">
            <Card className="p-6 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Plain English</div>
              <div className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{n.plain_english || "—"}</div>
            </Card>

            <Card className="p-6 border-border">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Draft response</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="mt-3 min-h-[220px] text-sm" data-testid="notice-draft" />
              <div className="mt-3 flex justify-end gap-2">
                <Button onClick={save} disabled={busy} data-testid="notice-save"><ShieldCheck className="h-4 w-4 mr-1.5" />Save</Button>
              </div>
            </Card>
          </div>

          <div className="space-y-5">
            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Sections cited</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(n.sections || []).map(s => <Badge key={s} variant="outline" className="font-mono">{s}</Badge>)}
                {(n.sections || []).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
              </div>
            </Card>
            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Missing documents</div>
              <ul className="mt-2 space-y-1">
                {(n.missing_documents || []).map((d, i) => <li key={i} className="text-xs">• {d}</li>)}
                {(n.missing_documents || []).length === 0 && <li className="text-xs text-muted-foreground">None flagged.</li>}
              </ul>
            </Card>
            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Required actions</div>
              <ul className="mt-2 space-y-1">
                {(n.required_actions || []).map((d, i) => <li key={i} className="text-xs">• {d}</li>)}
              </ul>
            </Card>
            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Risk reasons</div>
              <ul className="mt-2 space-y-1">
                {(n.risk_reasons || []).map((d, i) => <li key={i} className="text-xs">• {d}</li>)}
              </ul>
            </Card>
            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Timeline</div>
              <ul className="mt-2 space-y-1">
                {(n.history || []).slice(-8).map((h, i) => (
                  <li key={i} className="text-xs"><span className="font-mono">{new Date(h.at).toLocaleString()}</span> — {h.action}</li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
